import { useSyncExternalStore } from 'react';

export const REQUEST_HALT_ERROR_CODE = 'REQUESTS_HALTED';
export const REQUEST_HALT_MESSAGE = 'Работа приложения остановлена до перезагрузки страницы';

type HaltSource = 'axios' | 'rtk-query' | 'ws' | 'app';

export interface RequestHaltState {
  halted: boolean;
  message: string | null;
  status: number | null;
  source: HaltSource | null;
  triggeredAt: number | null;
}

interface HaltPayload {
  message?: string | null;
  status?: number | null;
  source?: HaltSource;
}

type HaltedRequestError = Error & {
  code: typeof REQUEST_HALT_ERROR_CODE;
  isRequestHalted: true;
};

const initialState: RequestHaltState = {
  halted: false,
  message: null,
  status: null,
  source: null,
  triggeredAt: null,
};

let state: RequestHaltState = initialState;
const listeners = new Set<() => void>();

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

export const getRequestHaltState = (): RequestHaltState => state;

export const isRequestHalted = (): boolean => state.halted;

export const shouldHaltForStatus = (status: unknown): status is number =>
  typeof status === 'number' && status >= 500;

export const haltRequests = ({ message, status, source = 'app' }: HaltPayload = {}): boolean => {
  if (state.halted) {
    return false;
  }

  state = {
    halted: true,
    message: message || REQUEST_HALT_MESSAGE,
    status: typeof status === 'number' ? status : null,
    source,
    triggeredAt: Date.now(),
  };
  emitChange();
  return true;
};

export const resetRequestHalt = (): void => {
  if (!state.halted) {
    return;
  }

  state = initialState;
  emitChange();
};

export const subscribeRequestHalt = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useRequestHalt = (): RequestHaltState =>
  useSyncExternalStore(subscribeRequestHalt, getRequestHaltState, getRequestHaltState);

export const createRequestHaltedError = (): HaltedRequestError => {
  const error = new Error(REQUEST_HALT_MESSAGE) as HaltedRequestError;
  error.name = 'RequestHaltedError';
  error.code = REQUEST_HALT_ERROR_CODE;
  error.isRequestHalted = true;
  return error;
};

export const isRequestHaltedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as {
    code?: string;
    isRequestHalted?: boolean;
    data?: { errorCode?: string };
    error?: string;
  };

  return Boolean(
    candidate.isRequestHalted ||
      candidate.code === REQUEST_HALT_ERROR_CODE ||
      candidate.data?.errorCode === REQUEST_HALT_ERROR_CODE ||
      candidate.error === REQUEST_HALT_ERROR_CODE
  );
};